import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { documentsApi } from 'api/documents';
import {
  Button,
  DocumentUpload,
  DocumentList,
  IDocumentItem,
  Valuation,
  PostOffer,
  Notes,
  FormattedNote,
} from 'components/common';
import { TabPanel } from 'components/TabPanel';
import { Tab, Tabs } from '@mui/material';
import { useApplicationByReferenceNumber } from 'queries';
import { useConveyancing } from 'queries/btlApplications';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsCommunicationTabEnabled, useIsTasksSystemEnabled } from 'providers/PosthogProvider';
import { IDetailesPageData, IDocumentFormatted } from 'queries/applications/useApplicationByReferenceNumber/types';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageURL } from 'router/pageURL';
import { filesTypesOptions, IUploadFileDto } from 'api/documents/types';
import { getMIMEByExtension, headerSections } from './helpers';
import { shouldShowValuationTab, shouldShowPostOfferTab } from './constants';
import { notesApi } from 'api/notes';
import { useCaseTasks } from 'queries/brokerCases/useCaseTasks';
import { usePrefetchDocumentReviews } from 'queries/brokerCases';
import { EvidenceTabContent } from 'pages/EvidencePage/EvidenceTabContent';
import { OverviewPage } from 'pages/OverviewPage';

export const ApplicationDetailsTab = {
  OVERVIEW: 'overview',
  EVIDENCE: 'evidence',
  DOCUMENTS: 'documents',
  COMMUNICATION: 'communication',
  VALUATION: 'valuation',
  POST_OFFER: 'post-offer',
} as const;

export type ApplicationDetailsTabValue = typeof ApplicationDetailsTab[keyof typeof ApplicationDetailsTab];

export const ApplicationDetailsPage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();

  const intl = useIntl();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const isCommunicationTabEnabled = useIsCommunicationTabEnabled();
  const isTasksSystemEnabled = useIsTasksSystemEnabled();

  const [activeTab, setActiveTab] = useState<ApplicationDetailsTabValue>(ApplicationDetailsTab.DOCUMENTS);

  const { data: application, isLoading } = useApplicationByReferenceNumber(params?.id || '');

  const caseId = application?.id;
  const valuation = application?.valuation;
  const { data: conveyancing, isLoading: isConveyancingLoading } = useConveyancing(caseId);

  const { data: caseTasksData, isLoading: isCaseTasksLoading } = useCaseTasks(caseId);

  const sections = caseTasksData?.sections ?? [];
  const showOverviewAndEvidenceTabs = isTasksSystemEnabled && sections.length > 0;

  usePrefetchDocumentReviews(typeof caseId === 'string' ? caseId : undefined, sections);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: ApplicationDetailsTabValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['analystApplications'] });
    };
  }, []);

  const hasValuation = useMemo(
    () => !!valuation && shouldShowValuationTab(application?.status || ''),
    [valuation, application?.status],
  );

  const isMigrated = useMemo(() => typeof application?.id !== 'number', [application?.id]);

  const hasConveyancing = useMemo(
    () => !isConveyancingLoading && !!conveyancing && shouldShowPostOfferTab(application?.status || ''),
    [isConveyancingLoading, conveyancing, application?.status],
  );

  const hasCommunication = useMemo(
    () => isMigrated && isCommunicationTabEnabled,
    [isMigrated, isCommunicationTabEnabled],
  );

  const prevShowOverviewAndEvidenceTabs = useRef(false);

  useEffect(() => {
    prevShowOverviewAndEvidenceTabs.current = false;
  }, [params?.id]);

  useEffect(() => {
    const isCommunicationSection = searchParams.get('section') === 'COMMUNICATION';

    if (hasCommunication && isCommunicationSection) {
      setActiveTab(ApplicationDetailsTab.COMMUNICATION);
    } else if (showOverviewAndEvidenceTabs && !prevShowOverviewAndEvidenceTabs.current) {
      setActiveTab(ApplicationDetailsTab.OVERVIEW);
    }

    prevShowOverviewAndEvidenceTabs.current = showOverviewAndEvidenceTabs;
  }, [searchParams, hasCommunication, showOverviewAndEvidenceTabs]);

  const { mutate: uploadFileApi, isLoading: isUploadFileLoading } = useMutation(documentsApi.uploadFile, {
    onSuccess: () => {
      enqueueSnackbar(intl.formatMessage({ id: 'applicationDetails.upload.success' }), { variant: 'success' });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['analystApplications'] }),
  });

  const { mutateAsync: createNote, isLoading: isCreatingNote } = useMutation(
    ({ title, content }: { title: string; content: string }) =>
      notesApi.createBtlNote(String(application!.id), { title, content, isInternal: false }),
    {
      onSuccess: () => {
        enqueueSnackbar(intl.formatMessage({ id: 'applicationDetails.note.create.success' }), { variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['analystApplications'] });
      },
      onError: (error: Error) => {
        enqueueSnackbar(error.message || 'Failed to create note', {
          variant: 'error',
        });
      },
    },
  );

  const handleCreateNote = async (title: string, content: string) => {
    if (isMigrated && application) {
      await createNote({ title, content });
    }
  };

  const formattedNotes: FormattedNote[] = useMemo(() => {
    if (!application?.notes) return [];
    return application.notes.map(note => ({
      id: note.id,
      title: note.title,
      content: note.content,
      createdAt: note.createdAt,
      creator: note.createdBy,
    }));
  }, [application?.notes]);

  if (isLoading || !application) return null;

  const applicationFiles = application.files;
  const detailsPageData: IDetailesPageData = {
    ...application,
    status: <FormattedMessage id={`applicationStatus.${application.status}`} />,
    financeAmount: <FormattedNumber value={application.financeAmount} />,
    ftv: <FormattedNumber value={application.ftv} style={'percent'} maximumFractionDigits={2} />,
  };

  const filesTypesOptionsTranslated = filesTypesOptions.map(optionString => ({
    value: optionString,
    label: intl.formatMessage({ id: `applicationDetails.upload.file.${optionString}` }),
  }));

  const headerSectionsTranslated = headerSections.map(optionString => ({
    key: optionString,
    value: intl.formatMessage({ id: `applicationDetails.header.${optionString}` }),
  }));

  const handleUpload = (files: File[], documentType: string) => {
    const filesData: IUploadFileDto[] = files.map(file => ({
      file,
      documentType,
    }));

    uploadFileApi({ filesData, applicationId: application.id });
  };

  const handleDocumentClick = (document: IDocumentItem) => {
    const resiDocument = document as IDocumentFormatted;
    const fileType = getMIMEByExtension(resiDocument.originalFileName || '');
    documentsApi.getFileById(resiDocument.id).then(res => {
      const url = window.URL.createObjectURL(new Blob([res], { type: fileType }));
      window.open(url);
    });
  };

  // Convert IDocumentFormatted to IDocumentItem format
  const mapToDocumentItems = (docs?: IDocumentFormatted[]): IDocumentItem[] => {
    if (!docs) return [];
    return docs.map(doc => ({
      id: doc.id,
      originalFileName: doc.originalFileName,
      createdAt: doc.createdAt,
      type: doc.type,
    }));
  };

  return (
    <main className='min-h-screen bg-gray-100'>
      <header className={'flex items-center justify-between bg-blue-100 px-8 py-4'}>
        <ul className={'m-0 flex list-none grid-cols-7 p-0'}>
          {headerSectionsTranslated.map(({ key, value }) => (
            <li key={`header-${key}`} className={'mr-8 shrink-0'}>
              <p className={'m-0 text-xs text-gray-500'}>{value}</p>
              <p className={'m-0 mt-1 text-sm'}>{detailsPageData[key] || '-'}</p>
            </li>
          ))}
        </ul>
        <Button variant={'outlined'} onClick={() => navigate(PageURL.APPLICATIONS)}>
          <FormattedMessage id={'cta.back'} />
        </Button>
      </header>
      <section className={'mx-auto mt-8 max-w-screen-xl'}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label='application tabs'>
          {showOverviewAndEvidenceTabs && <Tab label='Overview' value={ApplicationDetailsTab.OVERVIEW} />}
          {showOverviewAndEvidenceTabs && <Tab label='Evidence' value={ApplicationDetailsTab.EVIDENCE} />}
          {!showOverviewAndEvidenceTabs && (
            <Tab
              label={<FormattedMessage id={'applicationDetails.tabs.documents'} />}
              value={ApplicationDetailsTab.DOCUMENTS}
            />
          )}
          {hasCommunication && (
            <Tab
              label={<FormattedMessage id={'applicationDetails.tabs.communication'} />}
              value={ApplicationDetailsTab.COMMUNICATION}
            />
          )}
          {hasValuation && (
            <Tab
              label={<FormattedMessage id={'applicationDetails.tabs.valuation'} />}
              value={ApplicationDetailsTab.VALUATION}
            />
          )}
          {hasConveyancing && (
            <Tab
              label={<FormattedMessage id={'applicationDetails.tabs.postOffer'} />}
              value={ApplicationDetailsTab.POST_OFFER}
            />
          )}
        </Tabs>
        {showOverviewAndEvidenceTabs && (
          <TabPanel value={activeTab} index={ApplicationDetailsTab.OVERVIEW}>
            <div className='mt-6'>
              {application && (
                <OverviewPage
                  sections={sections}
                  applicationFilesInternal={applicationFiles.internal}
                  onGoToEvidenceV2={() => setActiveTab(ApplicationDetailsTab.EVIDENCE)}
                />
              )}
            </div>
          </TabPanel>
        )}
        {showOverviewAndEvidenceTabs && (
          <TabPanel value={activeTab} index={ApplicationDetailsTab.EVIDENCE}>
            <EvidenceTabContent
              caseId={caseId ? String(caseId) : ''}
              sections={sections}
              isCaseTasksLoading={isCaseTasksLoading}
            />
          </TabPanel>
        )}
        <TabPanel value={activeTab} index={ApplicationDetailsTab.DOCUMENTS}>
          <div className={'grid grid-cols-1 md:grid-cols-2 gap-8 mt-6'}>
            <div>
              <h5 className={'mt-0'}>Documents</h5>
              <div className={'flex flex-col gap-4'}>
                <DocumentList
                  documents={mapToDocumentItems(applicationFiles.internal)}
                  isLoading={false}
                  onDocumentClick={handleDocumentClick}
                  title={'Files'}
                />
                <DocumentList
                  documents={mapToDocumentItems(applicationFiles.external)}
                  isLoading={false}
                  onDocumentClick={handleDocumentClick}
                  title={'Uploaded documents'}
                />
              </div>
            </div>
            <div className={'w-full'}>
              <h5 className={'mt-0'}>
                <FormattedMessage id={'applicationDetails.actions.title'} />
              </h5>
              <DocumentUpload
                documentTypeOptions={filesTypesOptionsTranslated}
                isUploading={isUploadFileLoading}
                onUpload={handleUpload}
                direction={'column'}
              />
            </div>
          </div>
        </TabPanel>
        {hasCommunication && (
          <TabPanel value={activeTab} index={ApplicationDetailsTab.COMMUNICATION}>
            <div className={'mt-6'}>
              <Notes notes={formattedNotes} onCreateNote={handleCreateNote} isLoading={isCreatingNote} />
            </div>
          </TabPanel>
        )}
        {hasValuation && (
          <TabPanel value={activeTab} index={ApplicationDetailsTab.VALUATION}>
            <div className={'mt-6'}>
              <Valuation valuation={valuation} />
            </div>
          </TabPanel>
        )}
        {hasConveyancing && (
          <TabPanel value={activeTab} index={ApplicationDetailsTab.POST_OFFER}>
            <div className={'mt-6'}>
              <PostOffer conveyancing={conveyancing} />
            </div>
          </TabPanel>
        )}
      </section>
    </main>
  );
};
